import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import styled, { createGlobalStyle } from "styled-components";
import type { UploadItem } from "../types";
import { iconForItem, stampBarColor } from "../icons";
import { formatSize } from "../../utils";
import { row as rowCopy } from "../copy";
import ExtensionChip from "./ExtensionChip";
import UploadErrorCard from "./UploadErrorCard";

// Slower keyframes for v2 panel — unique names to coexist with v1 FileRow.
const RowAnimationsSlow = createGlobalStyle`
@keyframes RowStripeMoveSlow {
  from { background-position: 0 0; }
  to   { background-position: 40px 0; }
}
@keyframes RowSheenSweepSlow {
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes fadeInDown {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

interface ProgressProps {
  pct: number;
  isProcessing: boolean;
  isUploading: boolean;
}

const ProgressFill = styled.div<ProgressProps>(({ pct, isProcessing, isUploading }) => ({
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  borderRadius: "inherit",
  pointerEvents: "none",
  zIndex: 0,
  width: `${pct}%`,
  transition: "width 180ms ease-in-out",
  background: isProcessing
    ? "linear-gradient(90deg, rgba(124,77,255,0.18), rgba(124,77,255,0.08))"
    : "linear-gradient(90deg, rgba(139,214,109,0.22), rgba(139,214,109,0.10))",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundSize: "40px 40px",
    opacity: isUploading ? 0.6 : 0,
    animation: isUploading ? "RowStripeMoveSlow 1100ms linear infinite" : "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "28%",
    transform: "translateX(-120%)",
    background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)",
    mixBlendMode: "overlay",
    animation: isUploading ? "RowSheenSweepSlow 1800ms ease-in-out infinite" : "none",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&::before, &::after": { animation: "none" },
  },
}));

function subtitleFor(item: UploadItem, bytesUploaded: number): string {
  switch (item.status) {
    case "REQUESTED":
      if (item.declared_size) return formatSize(item.declared_size);
      return "";
    case "UPLOADED":
      return rowCopy.validating;
    case "PROCESSING":
      return rowCopy.processing;
    case "ACCEPTED":
      return item.declared_size ? formatSize(item.declared_size) : "";
    case "REJECTED":
      return item.error_code;
  }
}

interface UploadItemRowProps {
  item: UploadItem;
  depth: 0 | 1;
  bytesUploaded: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onRetry: () => Promise<void>;
  onRemove?: () => void;
  children?: React.ReactNode;
}

const UploadItemRowBase: React.FC<UploadItemRowProps> = ({
  item,
  depth,
  bytesUploaded,
  expanded,
  onToggleExpand,
  onRetry,
  onRemove,
  children,
}) => {
  const icon = iconForItem(item);
  const stamp = stampBarColor(item);
  const isRejected = item.status === "REJECTED";
  const isUploading = item.status === "REQUESTED" || item.status === "UPLOADED";
  const isProcessing = item.status === "PROCESSING";
  const isAccepted = item.status === "ACCEPTED";
  const pct = bytesUploaded > 0 ? Math.min(100, bytesUploaded) : 0;
  const hasChildren = children != null;
  const subtitle = subtitleFor(item, bytesUploaded);
  return (
    <Box sx={{ ml: depth === 1 ? "28px" : 0 }}>
      <RowAnimationsSlow />
      <Box
        sx={{
          position: "relative",
          borderRadius: "18px",
          background: "#F6FBF4",
          minHeight: "56px",
          overflow: "hidden",
          width: depth === 1 ? "94%" : "100%",
        }}
      >
        {/* Stamp bar */}
        {stamp && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "3px",
              background: stamp,
              borderRadius: "18px 0 0 18px",
              zIndex: 2,
            }}
          />
        )}

        {/* Progress fill */}
        <ProgressFill pct={pct} isProcessing={isProcessing} isUploading={isUploading} />

        {/* Row content */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            position: "relative",
            zIndex: 1,
            padding: "10px 14px 10px 12px",
            minHeight: "56px",
          }}
        >
          {/* Status icon */}
          <Box
            sx={{
              width: 20,
              height: 20,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon
              icon={icon.name}
              color={icon.color}
              width={20}
              style={{
                transition: "transform 200ms ease-out, opacity 200ms ease-out",
                ...(isProcessing && {
                  animation: "spin 4s linear infinite",
                }),
              }}
            />
          </Box>

          {/* Filename + subtitle */}
          <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
            <Tooltip title={item.filename} placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  lineHeight: "18px",
                  fontWeight: isAccepted || isProcessing ? 500 : 400,
                  color: "#0B1F15",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.filename}
              </Typography>
            </Tooltip>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px",
                  lineHeight: "16px",
                  fontWeight: 300,
                  color: isRejected ? "#C62828" : "#5B6B61",
                  fontFamily: isRejected ? "'Roboto Mono', monospace" : undefined,
                  textTransform: isRejected ? "uppercase" : undefined,
                }}
              >
                {subtitle}
              </Typography>
              {isAccepted && <ExtensionChip filename={item.filename} />}
            </Stack>
          </Stack>

          {/* Trailing slot */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            {isRejected && item.error_effect === "RETRY" && (
              <Tooltip title="Retry this file" placement="top">
                <Box
                  component="button"
                  onClick={onRetry as any}
                  aria-label="Retry this file"
                  sx={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    background: "#EF6C00",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": { background: "#E65100" },
                  }}
                >
                  <Icon icon="mdi:reload" color="white" width={16} />
                </Box>
              </Tooltip>
            )}
            {hasChildren && (
              <Box
                component="button"
                onClick={onToggleExpand}
                aria-label={expanded ? "Collapse zip children" : "Expand zip children"}
                sx={{
                  width: "28px",
                  height: "28px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  icon="mdi:chevron-down"
                  color="#5B6B61"
                  width={20}
                  style={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 200ms ease-out",
                  }}
                />
              </Box>
            )}
            {onRemove && depth === 0 && (
              <Tooltip title="Remove file" placement="top">
                <Box
                  component="button"
                  onClick={onRemove}
                  aria-label="Remove file"
                  sx={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "rgba(0,0,0,0.28)",
                    transition: "color 150ms, background 150ms",
                    "&:hover": {
                      color: "#D32F2F",
                      background: "rgba(211,47,47,0.08)",
                    },
                  }}
                >
                  <Icon icon="mdi:close" width={16} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Expanded error card */}
      {isRejected && expanded && (
        <UploadErrorCard item={item} onRetry={onRetry} />
      )}

      {/* Zip children */}
      {hasChildren && expanded && (
        <Box
          sx={{
            mt: 0.5,
            animation: "fadeInDown 300ms ease-out",
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};

const UploadItemRow = React.memo(UploadItemRowBase, (prev, next) =>
  prev.item === next.item &&
  prev.depth === next.depth &&
  prev.bytesUploaded === next.bytesUploaded &&
  prev.expanded === next.expanded &&
  (prev.onRemove === undefined) === (next.onRemove === undefined)
) as typeof UploadItemRowBase;

export default UploadItemRow;
